import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table, Button, Form, Input, Select, Space, Tag, Popconfirm, Tooltip, Switch, Row, Col,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, BankOutlined, SearchOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import CBModal from '../../components/CBModal';
import axiosInstance from '../../variables/api/axiosInstance';
import { can } from '../../variables/constants';
import { HAB } from '../../variables/habilitations';
import logoCarteBrune from '../../assets/images/logo.png';
import TableExportButton from '../../components/TableExportButton';
import {
  GREEN, API_PARAMETRAGE, TYPES_COMPTE, LIBELLE_TYPE, TagTypeCompte, LIBELLE_OPERATION,
} from './commun';

/**
 * Plan comptable — les comptes que mouvementent les écritures.
 *
 * Un compte déjà mouvementé ne se supprime pas et ne se renumérote pas : ce
 * serait réécrire des écritures passées. Il se désactive. Le serveur le refuse
 * de toute façon ; l'écran le dit avant.
 */
export default function ComptesComptables() {
  const { t } = useTranslation('comptabilite');
  const [comptes, setComptes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [parent, setParent] = useState(null);
  const [form] = Form.useForm();
  const modifiable = can(HAB.PARAM_COMPTABLE_UPDATE);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get(`${API_PARAMETRAGE}/comptes`);
      setComptes(r.data?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || t('comptes.messages.chargementImpossible'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { charger(); }, [charger]);

  const filtres = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    if (!q) return comptes;
    return comptes.filter((c) => `${c.numeroCompte} ${c.libelleCompte}`.toLowerCase().includes(q));
  }, [comptes, recherche]);

  const ouvrir = (record) => {
    setEditing(record || null);
    setParent(null);
    form.resetFields();
    form.setFieldsValue(record || { typeCompte: 'ACTIF', actif: true });
    setModalOpen(true);
  };

  /**
   * Le prochain numéro libre sous un compte : 4711 en portant déjà jusqu'à 471120
   * propose 471121. La largeur du suffixe est celle des enfants existants, pour ne
   * pas mélanger 47111 et 471101 dans la même série. Sans enfant, on part de 01 ;
   * si leur numérotation n'est pas chiffrée, on ne propose rien et on laisse saisir.
   */
  const prochainSousCompte = (numeroParent) => {
    const enfants = comptes
      .map((c) => c.numeroCompte)
      .filter((n) => n.length > numeroParent.length && n.startsWith(numeroParent));
    if (!enfants.length) return `${numeroParent}01`;
    const largeur = Math.min(...enfants.map((n) => n.length - numeroParent.length));
    const suffixes = enfants
      .map((n) => n.slice(numeroParent.length))
      .filter((suf) => suf.length === largeur && /^[0-9]+$/.test(suf))
      .map(Number);
    if (!suffixes.length) return null;
    const suivant = String(Math.max(...suffixes) + 1).padStart(largeur, '0');
    const propose = `${numeroParent}${suivant}`;
    return propose.length <= 10 ? propose : null;
  };

  /** Créer un compte sous un compte existant : rattachement et nature hérités. */
  const ouvrirSousCompte = (record) => {
    setEditing(null);
    setParent(record);
    form.resetFields();
    form.setFieldsValue({
      numeroCompte: prochainSousCompte(record.numeroCompte) || '',
      typeCompte: record.typeCompte,
      compteParentNumero: record.numeroCompte,
      actif: true,
    });
    setModalOpen(true);
  };

  const enregistrer = async (vals) => {
    try {
      if (editing) await axiosInstance.put(`${API_PARAMETRAGE}/comptes/${editing.id}`, vals);
      else await axiosInstance.post(`${API_PARAMETRAGE}/comptes`, vals);
      toast.success(editing ? t('comptes.messages.modifie') : t('comptes.messages.cree'));
      setModalOpen(false);
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('commun.enregistrementImpossible'));
    }
  };

  const supprimer = async (record) => {
    try {
      await axiosInstance.delete(`${API_PARAMETRAGE}/comptes/${record.id}`);
      toast.success(t('comptes.messages.supprime'));
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('commun.suppressionImpossible'));
    }
  };

  const colonnes = [
    { title: t('comptes.colonnes.numero'), dataIndex: 'numeroCompte', width: 120,
      sorter: (a, b) => a.numeroCompte.localeCompare(b.numeroCompte),
      defaultSortOrder: 'ascend',
      render: (v) => <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{v}</span> },
    { title: t('comptes.colonnes.nomCompte'), dataIndex: 'libelleCompte', ellipsis: true },
    { title: t('comptes.colonnes.rattacheA'), dataIndex: 'compteParentNumero', width: 110,
      render: (v) => (v
        ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</span>
        : <span style={{ color: '#c0c4cc' }}>—</span>),
      export: (v) => v || '' },
    { title: t('comptes.colonnes.nature'), dataIndex: 'typeCompte', width: 110,
      filters: TYPES_COMPTE.map((t) => ({ text: LIBELLE_TYPE[t], value: t })),
      onFilter: (v, r) => r.typeCompte === v,
      render: (v) => <TagTypeCompte type={v} />,
      export: (v) => LIBELLE_TYPE[v] || v },
    { title: t('comptes.colonnes.utilisePar'), key: 'usage', width: 300,
      render: (_, r) => {
        const regles = r.reglesUtilisatrices || [];
        if (!regles.length && !r.nombreEcritures) {
          return <span style={{ color: '#9ca3af', fontSize: 12 }}>{t('comptes.aucuneOperation')}</span>;
        }
        return (
          <Space size={4} wrap>
            {regles.map((t) => (
              <Tag key={t} color="geekblue" style={{ borderRadius: 20 }}>{LIBELLE_OPERATION[t] || t}</Tag>
            ))}
            {r.nombreEcritures > 0 && (
              <span style={{ color: '#6b7280', fontSize: 12 }}>
                {t('comptes.lignesPassees', { count: r.nombreEcritures })}
              </span>
            )}
          </Space>
        );
      },
      export: (_, r) => (r.reglesUtilisatrices || []).map((t) => LIBELLE_OPERATION[t] || t).join(', ') },
    { title: t('commun.statut'), dataIndex: 'actif', width: 90, align: 'center',
      render: (v) => (v
        ? <Tag color="green" style={{ borderRadius: 20 }}>{t('commun.actif')}</Tag>
        : <Tag style={{ borderRadius: 20 }}>{t('commun.inactif')}</Tag>),
      export: (v) => (v ? t('commun.actif') : t('commun.inactif')) },
  ];

  if (modifiable) {
    colonnes.push({
      title: t('commun.actions'), key: 'actions', width: 280, align: 'right', exportable: false,
      render: (_, r) => {
        const verrouille = r.nombreEcritures > 0 || (r.reglesUtilisatrices || []).length > 0;
        return (
          <Space size={6}>
            <Tooltip title={t('comptes.creerSousCompte', { numero: r.numeroCompte })}>
              <Button size="small" icon={<PlusOutlined />} onClick={() => ouvrirSousCompte(r)}
                style={{ borderRadius: 6 }}>
                {t('comptes.sousCompte')}
              </Button>
            </Tooltip>
            <Button size="small" icon={<EditOutlined />} onClick={() => ouvrir(r)}
              style={{ borderColor: GREEN, color: GREEN, borderRadius: 6 }}>
              {t('commun.modifier')}
            </Button>
            {verrouille ? (
              <Tooltip title={t('comptes.suppressionVerrouillee')}>
                <Button size="small" icon={<DeleteOutlined />} disabled style={{ borderRadius: 6 }} />
              </Tooltip>
            ) : (
              <Popconfirm title={t('comptes.confirmerSuppression')} onConfirm={() => supprimer(r)} okText={t('commun.oui')} cancelText={t('commun.non')}>
                <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 6 }} />
              </Popconfirm>
            )}
          </Space>
        );
      },
    });
  }

  // Un compte déjà mouvementé ne se renumérote pas : le champ est figé, avec la raison.
  const numeroFige = Boolean(editing && editing.nombreEcritures > 0);

  return (
    <Layout title={t('comptes.titre')}>
      <PageHeader
        titre={t('comptes.titre')}
        icone={<BankOutlined />}
        compte={loading && !comptes.length ? null : comptes.length}
        unite={t('comptes.unite')}
        actions={modifiable ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => ouvrir(null)}
            style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}>
            {t('comptes.nouveauCompte')}
          </Button>
        ) : null}
      />

      <div className="cb-card" style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input allowClear prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
          placeholder={t('comptes.placeholderRecherche')}
          style={{ flex: '1 1 320px', maxWidth: 520 }}
          onChange={(e) => setRecherche(e.target.value)} />
        <div style={{ flex: 1 }} />
        {/* UN SEUL BOUTON, AVEC SES OPTIONS. `TableExportButton` pose lui-meme le coin
            bas droit (`floating`), la reserve de place en bas de page, l'etat desactive
            quand il n'y a rien a exporter et le libelle de chargement - tout ce que les
            deux boutons poses a la main refaisaient a leur facon.

            Il ne transmettait pas `orientation` ni `logoPosition`, ce qui obligeait cet
            ecran a appeler `exportToPdf` directement : le plan comptable se lit en
            PAYSAGE - six colonnes dont deux longues, que le portrait ecrase - et porte le
            logo de la Carte Brune centre au-dessus des titres. Les deux options sont
            desormais passees par le composant. */}
        <TableExportButton
          floating
          data={filtres}
          columns={colonnes}
          filename="plan_comptable"
          title={t('comptes.export.titre')}
          subtitle={t('comptes.nbComptes', { count: filtres.length })}
          count={filtres.length}
          showTotals={false}
          orientation="landscape"
          logo={logoCarteBrune}
          logoPosition="center"
        />
      </div>

      <div className="cb-card" style={{ padding: 0 }}>
        <Table className="cb-table" dataSource={filtres} columns={colonnes} rowKey="id" loading={loading}
          pagination={{ pageSize: 25, showTotal: (n) => t('comptes.nbComptes', { count: n }) }} scroll={{ x: 900 }} />
      </div>

      <CBModal title={editing
        ? t('comptes.modale.titreModifier', { numero: editing.numeroCompte })
        : (parent ? t('comptes.modale.titreSousCompte', { numero: parent.numeroCompte, libelle: parent.libelleCompte }) : t('comptes.nouveauCompte'))}
        open={modalOpen} onCancel={() => setModalOpen(false)} footer={null} width={620} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={enregistrer} style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="numeroCompte" label={t('comptes.colonnes.numero')} rules={[{ required: true, message: t('comptes.modale.numeroRequis') }]}
                extra={numeroFige ? t('comptes.modale.numeroFige') : undefined}>
                <Input placeholder="5111" maxLength={10} disabled={numeroFige} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="libelleCompte" label={t('comptes.colonnes.nomCompte')} rules={[{ required: true, message: t('comptes.modale.nomRequis') }]}>
                <Input placeholder={t('comptes.modale.placeholderNom')} maxLength={200} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="typeCompte" label={t('comptes.colonnes.nature')} rules={[{ required: true }]}>
                <Select options={TYPES_COMPTE.map((t) => ({ value: t, label: LIBELLE_TYPE[t] }))}
                  getPopupContainer={(t) => t.parentElement} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="compteParentNumero" label={t('comptes.modale.compteRegroupement')}
                extra={parent ? t('comptes.modale.rattachementHerite') : undefined}>
                <Input placeholder="51" maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="actif" label={t('comptes.modale.compteActif')} valuePropName="checked"
                extra={t('comptes.modale.compteActifAide')}>
                <Switch checkedChildren={t('commun.oui')} unCheckedChildren={t('commun.non')} />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
            <Button onClick={() => setModalOpen(false)}>{t('commun.annuler')}</Button>
            <Button type="primary" htmlType="submit" style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}>
              {editing ? t('commun.enregistrer') : t('comptes.modale.creerCompte')}
            </Button>
          </div>
        </Form>
      </CBModal>
    </Layout>
  );
}
